<script setup>
import { ref, computed, nextTick, onMounted } from 'vue';
import { useStore } from 'vuex';
import MarkdownIt from 'markdown-it';
import sicSupportAPI from 'dashboard/api/sicSupport';

const store = useStore();
const md = new MarkdownIt({ html: false, breaks: true, linkify: true });

// Panel state
const isOpen = ref(false);
const isLoading = ref(false);

// Messages: [{ role: 'user'|'assistant', content: '', images: [] }]
const messages = ref([]);

// Input state
const inputText = ref('');
const pendingImages = ref([]); // [{ dataUrl, mimeType }]
const fileInputRef = ref(null);
const messagesEndRef = ref(null);
const textareaRef = ref(null);

// Sync status (admin only)
const syncStatus = ref(null); // { synced_chunks, synced_at }
const isSyncing = ref(false);

const currentRole = computed(() => store.getters['auth/getCurrentRole']);
const isAdmin = computed(() => currentRole.value === 'administrator');

// History for API — last 10 exchanges (user + assistant pairs), no images
const apiHistory = computed(() => {
  return messages.value.slice(-20).map(m => ({
    role: m.role,
    content: m.content,
  }));
});

function open() {
  isOpen.value = true;
  if (messages.value.length === 0) {
    messages.value.push({
      role: 'assistant',
      content:
        'Olá! 👋 Sou o IntelliChat, seu assistente de suporte do SIC. Como posso te ajudar hoje?\n\nVocê pode me perguntar sobre qualquer funcionalidade do sistema ou enviar prints de telas — estou aqui para te guiar.',
    });
  }
  nextTick(scrollToBottom);
}

function close() {
  isOpen.value = false;
}

function toggle() {
  if (isOpen.value) {
    close();
  } else {
    open();
  }
}

function scrollToBottom() {
  messagesEndRef.value?.scrollIntoView({ behavior: 'smooth' });
}

function renderMarkdown(text) {
  return md.render(text || '');
}

// Image handling
function onFileChange(e) {
  const files = Array.from(e.target.files || []);
  files.forEach(readImageFile);
  e.target.value = '';
}

function onPaste(e) {
  const items = Array.from(e.clipboardData?.items || []);
  items.forEach(item => {
    if (item.type.startsWith('image/')) {
      readImageFile(item.getAsFile());
    }
  });
}

function readImageFile(file) {
  if (!file) return;
  const reader = new FileReader();
  reader.onload = ev => {
    pendingImages.value.push({ dataUrl: ev.target.result, mimeType: file.type });
  };
  reader.readAsDataURL(file);
}

function removeImage(index) {
  pendingImages.value.splice(index, 1);
}

// Send message
async function send() {
  const text = inputText.value.trim();
  const imgs = pendingImages.value.map(i => i.dataUrl);

  if (!text && imgs.length === 0) return;
  if (isLoading.value) return;

  // Add user message to chat
  messages.value.push({
    role: 'user',
    content: text,
    images: [...imgs],
  });

  inputText.value = '';
  pendingImages.value = [];
  isLoading.value = true;
  await nextTick(scrollToBottom);

  try {
    const response = await sicSupportAPI.chat({
      message: text,
      images: imgs,
      history: apiHistory.value.slice(0, -1), // exclude the message just added
    });

    messages.value.push({
      role: 'assistant',
      content: response.data?.reply || 'Não consegui gerar uma resposta.',
    });
  } catch {
    messages.value.push({
      role: 'assistant',
      content: 'Erro ao conectar com o assistente. Por favor, tente novamente.',
    });
  } finally {
    isLoading.value = false;
    await nextTick(scrollToBottom);
  }
}

function onKeydown(e) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    send();
  }
}

// Admin: sync knowledge base
async function syncKnowledge() {
  if (isSyncing.value) return;
  isSyncing.value = true;
  try {
    const res = await sicSupportAPI.syncKnowledge();
    syncStatus.value = res.data;
  } catch (err) {
    syncStatus.value = { error: err?.response?.data?.error || 'Erro ao sincronizar.' };
  } finally {
    isSyncing.value = false;
  }
}

// Open screenshot images in a new tab when clicked
function onBubbleClick(event) {
  const img = event.target;
  if (img.tagName === 'IMG' && img.src) {
    window.open(img.src, '_blank');
  }
}

onMounted(() => {
  // Auto-focus textarea when panel opens
});
</script>

<template>
  <!-- Floating bubble -->
  <div class="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
    <!-- Expanded panel -->
    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 translate-y-4 scale-95"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-4 scale-95"
    >
      <div
        v-if="isOpen"
        class="flex w-[380px] flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-background shadow-2xl"
        style="height: 520px"
      >
        <!-- Header -->
        <div
          class="flex shrink-0 items-center gap-3 border-b border-n-weak bg-n-background px-4 py-3"
        >
          <div
            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-woot-500"
          >
            <span class="i-lucide-bot h-4 w-4 text-white" />
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold text-n-slate-12 leading-tight">
              IntelliChat
            </p>
            <p class="text-xs text-n-slate-9 leading-tight">Assistente de IA</p>
          </div>
          <button
            class="flex h-7 w-7 items-center justify-center rounded-lg text-n-slate-10 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
            @click="close"
          >
            <span class="i-lucide-x h-4 w-4" />
          </button>
        </div>

        <!-- Messages list -->
        <div class="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          <div
            v-for="(msg, i) in messages"
            :key="i"
            class="flex"
            :class="msg.role === 'user' ? 'justify-end' : 'justify-start'"
          >
            <!-- Assistant avatar -->
            <div
              v-if="msg.role === 'assistant'"
              class="mr-2 mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-woot-100"
            >
              <span class="i-lucide-bot h-3.5 w-3.5 text-woot-600" />
            </div>

            <div class="max-w-[80%]">
              <!-- Images preview (user only) -->
              <div
                v-if="msg.images?.length"
                class="mb-1.5 flex flex-wrap gap-1.5 justify-end"
              >
                <img
                  v-for="(src, idx) in msg.images"
                  :key="idx"
                  :src="src"
                  class="h-20 w-20 rounded-lg object-cover border border-n-weak"
                  alt=""
                />
              </div>

              <!-- Text bubble -->
              <div
                v-if="msg.content"
                class="rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed"
                :class="
                  msg.role === 'user'
                    ? 'rounded-tr-sm bg-woot-500 text-white'
                    : 'rounded-tl-sm bg-n-alpha-2 text-n-slate-12'
                "
              >
                <!-- User: plain text -->
                <span v-if="msg.role === 'user'">{{ msg.content }}</span>
                <!-- Assistant: rendered markdown -->
                <!-- eslint-disable-next-line vue/no-v-html -->
                <div
                  v-else
                  class="prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-img:my-2 prose-img:rounded-lg prose-img:border prose-img:border-n-weak prose-img:shadow-md prose-img:max-w-full prose-img:cursor-pointer"
                  v-html="renderMarkdown(msg.content)"
                  @click="onBubbleClick($event)"
                />
              </div>
            </div>
          </div>

          <!-- Typing indicator -->
          <div v-if="isLoading" class="flex justify-start">
            <div
              class="mr-2 mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-woot-100"
            >
              <span class="i-lucide-bot h-3.5 w-3.5 text-woot-600" />
            </div>
            <div
              class="flex items-center gap-1.5 rounded-2xl rounded-tl-sm bg-n-alpha-2 px-4 py-3"
            >
              <span
                class="h-2 w-2 rounded-full bg-n-slate-9 animate-bounce"
                style="animation-delay: 0ms"
              />
              <span
                class="h-2 w-2 rounded-full bg-n-slate-9 animate-bounce"
                style="animation-delay: 150ms"
              />
              <span
                class="h-2 w-2 rounded-full bg-n-slate-9 animate-bounce"
                style="animation-delay: 300ms"
              />
            </div>
          </div>

          <!-- Scroll anchor -->
          <div ref="messagesEndRef" />
        </div>

        <!-- Pending image previews -->
        <div
          v-if="pendingImages.length"
          class="flex shrink-0 gap-2 overflow-x-auto border-t border-n-weak px-4 py-2"
        >
          <div
            v-for="(img, i) in pendingImages"
            :key="i"
            class="relative shrink-0"
          >
            <img
              :src="img.dataUrl"
              class="h-14 w-14 rounded-lg object-cover border border-n-weak"
              alt=""
            />
            <button
              class="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-n-slate-12 text-white"
              @click="removeImage(i)"
            >
              <span class="i-lucide-x h-3 w-3" />
            </button>
          </div>
        </div>

        <!-- Admin sync bar -->
        <div
          v-if="isAdmin"
          class="flex shrink-0 items-center gap-2 border-t border-n-weak bg-n-alpha-1 px-4 py-2"
        >
          <span class="i-lucide-database h-3.5 w-3.5 text-n-slate-9" />
          <span class="flex-1 truncate text-xs text-n-slate-9">
            <span v-if="syncStatus?.synced_chunks">
              {{ syncStatus.synced_chunks }} chunks sincronizados
            </span>
            <span v-else-if="syncStatus?.error" class="text-red-500">
              {{ syncStatus.error }}
            </span>
            <span v-else>Base de conhecimento</span>
          </span>
          <button
            class="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-woot-600 transition-colors hover:bg-woot-50 disabled:opacity-50"
            :disabled="isSyncing"
            @click="syncKnowledge"
          >
            <span
              class="h-3 w-3"
              :class="
                isSyncing
                  ? 'i-lucide-loader-circle animate-spin'
                  : 'i-lucide-refresh-cw'
              "
            />
            {{ isSyncing ? 'Sincronizando...' : 'Sync' }}
          </button>
        </div>

        <!-- Input area -->
        <div class="shrink-0 border-t border-n-weak px-3 py-3">
          <div
            class="flex items-end gap-2 rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 focus-within:border-woot-400 focus-within:ring-1 focus-within:ring-woot-400/30"
          >
            <textarea
              ref="textareaRef"
              v-model="inputText"
              rows="1"
              placeholder="Pergunte sobre o SIC..."
              class="flex-1 resize-none bg-transparent text-sm text-n-slate-12 placeholder-n-slate-9 outline-none"
              style="max-height: 80px; min-height: 22px; overflow-y: auto; field-sizing: content"
              :disabled="isLoading"
              @keydown="onKeydown"
              @paste="onPaste"
            />

            <!-- Attach image -->
            <input
              ref="fileInputRef"
              type="file"
              accept="image/*"
              multiple
              class="hidden"
              @change="onFileChange"
            />
            <button
              class="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-n-slate-9 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
              title="Anexar imagem"
              :disabled="isLoading"
              @click="fileInputRef?.click()"
            >
              <span class="i-lucide-image h-4 w-4" />
            </button>

            <!-- Send -->
            <button
              class="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors disabled:opacity-40"
              :class="
                inputText.trim() || pendingImages.length
                  ? 'bg-woot-500 text-white hover:bg-woot-600'
                  : 'text-n-slate-9'
              "
              :disabled="isLoading || (!inputText.trim() && !pendingImages.length)"
              title="Enviar (Enter)"
              @click="send"
            >
              <span class="i-lucide-send h-3.5 w-3.5" />
            </button>
          </div>
          <p class="mt-1 text-center text-[10px] text-n-slate-7">
            Enter para enviar · Shift+Enter para nova linha
          </p>
        </div>
      </div>
    </Transition>

    <!-- Bubble toggle button -->
    <button
      class="flex h-14 w-14 items-center justify-center rounded-full bg-woot-500 text-white shadow-lg transition-all hover:bg-woot-600 hover:scale-105 active:scale-95"
      :title="isOpen ? 'Fechar suporte' : 'Abrir suporte SIC'"
      @click="toggle"
    >
      <Transition
        enter-active-class="transition-all duration-150"
        enter-from-class="opacity-0 scale-50 rotate-90"
        enter-to-class="opacity-100 scale-100 rotate-0"
        leave-active-class="transition-all duration-150"
        leave-from-class="opacity-100 scale-100 rotate-0"
        leave-to-class="opacity-0 scale-50 rotate-90"
        mode="out-in"
      >
        <span
          v-if="isOpen"
          key="close"
          class="i-lucide-x h-6 w-6"
        />
        <span
          v-else
          key="open"
          class="i-lucide-circle-help h-6 w-6"
        />
      </Transition>
    </button>
  </div>
</template>
